import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AdminLayout, ActionDropdown, ConfirmModal } from '../../components';
import api from '../../services/api';
import { formatCourseThumbnail, handleThumbnailError } from '../../utils/imageUtils';
import { formatDate } from '../../utils/dateUtils';
import SEO from '../../components/common/SEO';

const AdminCourseManagement = () => {
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [confirm, setConfirm] = useState({ open: false, title: '', message: '', variant: 'danger', icon: 'warning', confirmText: '', onConfirm: null });

  useEffect(() => { fetchCourses(); }, [searchTerm]);

  const fetchCourses = async () => {
    setLoading(true);
    try {
      const data = await api.getCourses({ page: 1, limit: 100, ...(searchTerm && { search: searchTerm }) });
      setCourses(data.courses || []);
    } catch (error) {
      console.error('Error fetching courses:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePublish = (course) => {
    const isPublished = course.status === 'published';
    setConfirm({
      open: true,
      title: isPublished ? 'Unpublish Course?' : 'Publish Course?',
      message: isPublished
        ? `"${course.title}" will no longer be visible to students.`
        : `"${course.title}" will be visible to all students.`,
      variant: isPublished ? 'warning' : 'info',
      icon: 'warning',
      confirmText: isPublished ? 'Unpublish' : 'Publish',
      onConfirm: async () => {
        try {
          await api.publishCourse(course._id || course.id);
          fetchCourses();
        } catch (error) {
          alert(error.message || 'Failed to toggle publish status');
        }
        setConfirm(prev => ({ ...prev, open: false }));
      },
    });
  };

  const handleDelete = (course) => {
    setConfirm({
      open: true,
      title: 'Delete Course?',
      message: `"${course.title}" and all its classes will be permanently deleted. This action cannot be undone.`,
      variant: 'danger',
      icon: 'delete',
      confirmText: 'Delete Permanently',
      onConfirm: async () => {
        try {
          await api.deleteCourse(course._id || course.id);
          fetchCourses();
        } catch (error) {
          alert(error.message || 'Failed to delete course');
        }
        setConfirm(prev => ({ ...prev, open: false }));
      },
    });
  };

  const filteredCourses = courses.filter((course) => {
    const matchesFilter = filter === 'all' || course.status === filter;
    const matchesSearch = !searchTerm ||
      course.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      course.teacher?.name?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const getActions = (course) => [
    {
      label: 'Edit Course',
      icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>,
      onClick: () => navigate(`/admin/courses/edit/${course._id || course.id}`),
    },
    {
      label: 'View Course',
      icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>,
      onClick: () => window.open(`/courses/${course._id || course.id}`, '_blank'),
    },
    {
      label: course.status === 'published' ? 'Unpublish' : 'Publish',
      icon: course.status === 'published'
        ? <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" /></svg>
        : <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>,
      variant: course.status === 'published' ? 'warning' : 'success',
      onClick: () => handleTogglePublish(course),
      divider: true,
    },
    {
      label: 'Delete',
      icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>,
      variant: 'danger',
      onClick: () => handleDelete(course),
    },
  ];

  return (
    <AdminLayout>
      <SEO title="Course Management" noIndex />
      <div className="mb-8 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-dark-900 mb-2">Course Management</h1>
          <p className="text-dark-500">Review and manage all platform courses</p>
        </div>
        <Link to="/admin/courses/create" className="btn-primary inline-flex items-center gap-2 self-start">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          Create Course
        </Link>
      </div>

      <div className="card p-4 mb-6 flex flex-col sm:flex-row gap-4 items-center justify-between">
        <div className="flex gap-2">
          {['all', 'published', 'draft'].map((f) => (
            <button key={f} onClick={() => setFilter(f)}
              className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${filter === f ? 'bg-primary-500 text-white' : 'bg-dark-100 text-dark-600 hover:bg-dark-200'}`}>
              {f.charAt(0).toUpperCase() + f.slice(1)} ({f === 'all' ? courses.length : courses.filter(c => c.status === f).length})
            </button>
          ))}
        </div>
        <div className="relative">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-dark-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input type="text" placeholder="Search courses or teachers..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-4 py-2 w-64 border border-dark-200 rounded-lg focus:outline-none focus:border-primary-500" />
        </div>
      </div>

      <div className="card overflow-hidden min-h-[320px]">
        {loading ? (
          <div className="p-12 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto" />
            <p className="mt-4 text-dark-500">Loading courses...</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-dark-50">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-dark-900">Course</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-dark-900">Teacher</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-dark-900">Category</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-dark-900">Classes</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-dark-900">Status</th>
                  <th className="px-6 py-4 text-right text-sm font-semibold text-dark-900 w-16">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-dark-100">
                {filteredCourses.map((course) => (
                  <tr key={course._id || course.id} className="hover:bg-dark-50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <img src={formatCourseThumbnail(course.thumbnail)} alt={course.title}
                          className="w-12 h-12 rounded object-cover" onError={handleThumbnailError} />
                        <div>
                          <div className="font-medium text-dark-900">{course.title}</div>
                          <div className="text-sm text-dark-500">{formatDate(course.createdAt)}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-dark-600">{course.teacher?.name || 'Unassigned'}</td>
                    <td className="px-6 py-4">
                      {course.category ? (
                        <span className="bg-dark-100 text-dark-700 px-2 py-1 rounded text-sm">{course.category}</span>
                      ) : <span className="text-dark-400">—</span>}
                    </td>
                    <td className="px-6 py-4 text-dark-600">{course.liveClassCount || 0}</td>
                    <td className="px-6 py-4">
                      <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${course.status === 'published' ? 'bg-green-100 text-green-600' : 'bg-yellow-100 text-yellow-600'}`}>
                        {course.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <ActionDropdown actions={getActions(course)} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!loading && filteredCourses.length === 0 && (
          <div className="p-12 text-center">
            <svg className="w-16 h-16 text-dark-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
            </svg>
            <h3 className="text-lg font-semibold text-dark-900 mb-2">No courses found</h3>
            <p className="text-dark-500 mb-4">Try adjusting your search or filter criteria.</p>
            <Link to="/admin/courses/create" className="btn-primary inline-flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              Create Your First Course
            </Link>
          </div>
        )}
      </div>

      <ConfirmModal
        open={confirm.open}
        onClose={() => setConfirm(prev => ({ ...prev, open: false }))}
        onConfirm={confirm.onConfirm}
        title={confirm.title}
        message={confirm.message}
        confirmText={confirm.confirmText}
        variant={confirm.variant}
        icon={confirm.icon}
      />
    </AdminLayout>
  );
};

export default AdminCourseManagement;
